import { getApiUrl } from '../constants/config'
import type { SlotContributionDTO } from '../slots/registerExternal'

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface Workspace {
  id: string
  user_id: string
  name: string
  created_at: number
  last_activity?: number
}

export interface Chat {
  id: string
  workspace_id: string
  name: string
  command: string
  agent_plugin: string | null
  agent_session_id: string | null
  connection_type: 'pty' | 'api' | 'cli'
  model_id: string | null
  agent_display_name: string | null
  /** Per-chat render preference; null = use the plugin's capability default. */
  view_mode?: 'clean' | 'whimerclean' | 'whimerterm' | 'terminal' | 'native' | 'clean3' | 'term3' | null
  /** 0/1 — pinned to the top of the chat list. */
  pinned?: number
  /** How the agent is spawned (Phase C): 'tmux' (v1 default) | 'native-pty' (direct PTY). */
  spawn_mode?: 'tmux' | 'native-pty'
  state: string
  created_at: number
  last_activity: number
  hasActivePty?: boolean
  isActive?: boolean
  /** Owning workspace name — only present on the cross-workspace "all chats" list. */
  workspace_name?: string
  /** pane.state seed from GET /api/chats (spec/CHAT_API.md) — chat-list badge on load; the
   *  global `pane_state` WS keeps it live after. null when no native session has run. */
  paneState?: { state: 'running' | 'waiting' | 'idle' | 'error'; since: number; detail?: unknown } | null
  /** Origin of the chat — 'user' (created in the UI) or 'api' (provisioned via the API). */
  created_by?: 'user' | 'api'
  /** Per-chat API token (spec/CHAT_API.md §5); null/absent = none issued. */
  api_token?: string | null
}

export interface PluginSetting {
  name: string
  displayName: string
  isInternal: boolean
  canResume: boolean
  defaultCwd: string | null
  currentCwd: string | null
}

export interface ApiKeyRow {
  name: string
  nickname: string | null
  category: string
  model: string | null
  preview: string
  created_at: number
}

export interface Provider {
  id: string
  category: string
  label: string
  model: string
}

// External plugin / extension (folder-based plugins/external/<name>/extension.md).
export interface ExtensionInfo {
  name: string
  displayName: string
  description: string | null
  version: string | null
  author: string | null
  enabled: boolean
  updateCheckUrl: string | null
}

export interface ExtensionUpdateCheck {
  hasUpdate: boolean
  current: string | null
  latest: string | null
}

// Internal plugin (folder-discovered recognizer) — enable/disable (PLUGIN_MODULES.md Phase 1).
// `mandatory` recognizers (recognizer:prompt) are load-bearing: the toggle is shown disabled.
export interface RecognizerInfo {
  name: string
  displayName: string
  mandatory: boolean
  slot: string | null
  enabled: boolean
}

// Internal feature plugin (voice, …) — enable/disable (PLUGIN_MODULES.md Phase 2).
export interface FeaturePluginInfo {
  id: string
  displayName: string
  description: string
  enabled: boolean
}

export interface VoiceConfig {
  liveCallActiveKey: string | null
  ttsActiveKey:      string | null
  sttActiveKey:      string | null
  notesDir:          string | null
}

export interface Message {
  id: number
  chat_id: string
  direction: string
  raw_content: string
  clean_content: string
  timestamp: number
}

export type TurnRole = 'user' | 'assistant' | 'thinking' | 'tool_call' | 'tool_result' | 'info'

export interface ConversationTurn {
  id:          string
  role:        TurnRole
  content:     string
  toolName?:   string
  toolInput?:  string
  toolCallId?: string
  timestamp?:  string
  // Stable identity across a JSONL rewrite (from the entry uuid). `id` is byte-offset-derived
  // and shifts on rewrite, so dedup/reconcile must key on srcKey to avoid double-render (BUG-024).
  srcKey?:     string
  status?:     'sending' | 'failed' | 'queued'
}

export interface StructuredHistory {
  source:    string        // plugin name, or 'db'
  sessionId: string | null
  turns:     ConversationTurn[]
  total?:        number    // total turns available in the session
  oldestLoaded?: number    // index (from start) of the first turn in `turns` — cursor for loading older
  hasMore?:      boolean   // older turns exist before `oldestLoaded`
}

export interface AgentCapabilities {
  interactive: boolean
  cleanView: boolean
  liveStream: boolean
  streamStrategy: 'structured' | 'plaintext' | 'screen-scrape'
  /** Whether the UI shows the live terminal zone. undefined/true = show; false = committed-only
   *  (e.g. grok — unreadable live frame). Declared per-plugin (built-in or external extension.md). */
  liveView?: boolean
}

// Interactive prompt detected in the agent's terminal (permission box, menu, y/n).
// Surfaced as tappable buttons in the chat so the user can answer without a keyboard.
export interface PromptOption {
  key:   string   // short button text (e.g. "1", "Yes")
  label: string   // full option text
  send:  string   // keystrokes written back to the PTY when tapped
  desc?: string   // the option's wrapped description/blurb, if any
  input?: boolean // free-text option ("Type something.") → render as an input box + send
  checked?: boolean // multi-select only: whether this option's checkbox is currently ticked
}
export interface DetectedPrompt {
  id:       string
  question: string
  options:  PromptOption[]
  kind:     'numbered' | 'yesno' | 'arrow'
  raw?:     string[]   // raw text of the claimed rows — shown in the card for in-place debug
  multi?:     boolean  // multi-select (checkbox list): toggle with Space + a Submit button
  selIndex?:  number   // cursor (❯) option index — card navigates relative to this to toggle
  submitSend?: string  // keystrokes that submit a multi-select (Enter by default)
}

// WhimerTerm (3rd view) — the recognized volatile bottom of the terminal, split into
// 3 zones anchored on the input box. Mirrors backend whimerterm/liveFrame.ts.
export type LiveWidget =
  | { kind: 'spinner'; label: string; hint?: string }
  | { kind: 'prompt';  prompt: DetectedPrompt }
  | { kind: 'menu';    title?: string; items: { text: string; send: string }[] }
  | { kind: 'todo';    items: { text: string; state: 'done' | 'doing' | 'todo' }[] }
  | { kind: 'status';  fields: { value: string }[] }
  | { kind: 'note';    text: string }
  | { kind: 'raw';     lines: string[] }
export interface LiveFrame {
  signature:  string
  aboveInput: LiveWidget[]
  composer:   { boxText: string; placeholder?: string } | null
  belowInput: LiveWidget[]
}

// ─── Native v2 pipeline (WhimerTerm² / "native" view) ──────────────────────────────
// Mirrors the backend native stack EXACTLY:
//   native/whimer-vt/types.ts  → NativeStyle / NativeSegment / NativeLine
//   native/core/plugin.ts      → NativeWidget (Widget: kind/zone/align/order/data)
//   native/session.ts          → NativeIR ({ widgets })
// Two WS messages carry these: `native_commit` (Line[] of styled segments — the settled
// log) and `native_frame` ({ ir: { widgets } } — the volatile live region).

/** Per-cell visual attributes. Colors = css tokens ('red' | '#rrggbb' | 'ansi-3'). */
export interface NativeStyle {
  fg?: string
  bg?: string
  bold?: boolean
  dim?: boolean
  italic?: boolean
  underline?: boolean
  inverse?: boolean
}
/** A run of consecutive same-style text. */
export interface NativeSegment {
  text:  string
  style: NativeStyle
  col:   number   // 0-based start column
}
/** A logical line = styled segments (committed or live). */
export interface NativeLine {
  segments: NativeSegment[]
}
/** Semantic widget produced by a recognizer (position is a hint — client reflows, R17). */
export interface NativeWidget {
  kind:   'spinner' | 'prompt' | 'menu' | 'table' | 'tool-call' | 'streaming' | 'progress' | 'corner' | 'composer' | 'raw'
  zone?:  'above-input' | 'input' | 'below-input' | 'overlay'
  align?: 'left' | 'right' | 'center'
  order?: number
  data?:  unknown   // kind-specific payload
  committed?: boolean // backend JSONL-oracle: this live prose is already a committed turn → views may hide it
}
/** The assembled live IR carried by a `native_frame`. */
export interface NativeIR {
  widgets: NativeWidget[]
}

export interface Plugin {
  name: string
  displayName: string
  isInternal: boolean
  canResume: boolean
  capabilities?: AgentCapabilities
  /** Presentation hints declared by the plugin (C4): tag color/icon. Omit → agentTag()
   *  derives a stable color from `name`, so the UI never hardcodes a per-agent color map. */
  ui?: { color?: string; icon?: string }
  /** Phase F switcher: this plugin can act as a HARNESS for another brain (declares an env-mapping). */
  switchable?: boolean
  /** Phase F switcher: this agent's brain id when run under another harness (key into switcher routes). */
  switcherModel?: string
}

/** Phase F งาน3: one routed model available to the switcher (allowlist-filtered by the backend).
 *  Drives the "Model" picker that appears when a non-Native HarnessCLI is chosen. */
export interface SwitcherRoute {
  model:          string
  provider?:      string
  upstreamModel?: string
}

export interface AvailableSession {
  sessionId:        string
  title:            string | null
  firstUserMessage: string | null
  lastActivity:     number
  turnCount:        number
  linkedChatId:     string | null
}

// ─── Token helpers ─────────────────────────────────────────────────────────────

const TOKEN_KEY = 'whimer_jwt'

function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

// ─── Core request ──────────────────────────────────────────────────────────────

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = getToken()
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(`${getApiUrl()}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error(err.error || res.statusText)
  }

  if (res.status === 204) return undefined as T
  return res.json()
}

// ─── API surface ───────────────────────────────────────────────────────────────

export const api = {
  auth: {
    status: () =>
      request<{ setup: boolean }>('GET', '/auth/status'),
    setup: () =>
      request<{ qrCodeDataUrl: string; secret: string; username: string }>('POST', '/auth/setup'),
    verifySetup: (token: string) =>
      request<{ jwt: string }>('POST', '/auth/verify-setup', { token }),
    login: (token: string) =>
      request<{ jwt: string }>('POST', '/auth/login', { token }),
    me: () =>
      request<{ id: string; username: string }>('GET', '/auth/me'),
    // Sign out ALL devices: server bumps token_version → every JWT (including ours) dies.
    logoutAll: () =>
      request<{ ok: boolean }>('POST', '/auth/logout-all'),
  },
  workspaces: {
    list: () =>
      request<Workspace[]>('GET', '/api/workspaces'),
    create: (name: string) =>
      request<Workspace>('POST', '/api/workspaces', { name }),
    delete: (id: string) =>
      request<void>('DELETE', `/api/workspaces/${id}`),
  },
  chats: {
    list: (wsId: string) =>
      request<Chat[]>('GET', `/api/workspaces/${wsId}/chats`),
    /** All chats for the authed user across every workspace (Recent view). */
    listAll: () =>
      request<Chat[]>('GET', '/api/chats'),
    /** Resolve-or-create the user's "General" workspace for workspace-less chats. */
    generalWorkspace: () =>
      request<Workspace>('GET', '/api/general-workspace'),
    create: (wsId: string, body: {
      name?: string
      command?: string
      agentPlugin?: string
      agentSessionId?: string
      connection_type?: 'pty' | 'api' | 'cli'
      model_id?: string
      agent_display_name?: string
      /** Phase C: 'native-pty' spawns a direct PTY (honoured only when WHIMER_NATIVE on). */
      spawnMode?: 'tmux' | 'native-pty'
    }) =>
      request<Chat & { resumed?: boolean }>('POST', `/api/workspaces/${wsId}/chats`, body),
    availableSessions: (params?: { plugin?: string; cwd?: string; limit?: number }) => {
      const q = new URLSearchParams()
      q.set('limit', String(params?.limit ?? 20))
      if (params?.plugin) q.set('plugin', params.plugin)
      if (params?.cwd) q.set('cwd', params.cwd)
      return request<{ cwd: string; sessions: AvailableSession[] }>(
        'GET',
        `/api/sessions/available?${q.toString()}`,
      )
    },
    get: (id: string) =>
      request<Chat>('GET', `/api/chats/${id}`),
    /** Upload an attached image; returns its absolute server path (handed to the agent). */
    uploadImage: (id: string, body: { name: string; mime: string; base64: string }) =>
      request<{ path: string; name: string; size: number }>('POST', `/api/chats/${id}/upload`, body),
    update: (id: string, data: Partial<Chat>) =>
      request<Chat>('PATCH', `/api/chats/${id}`, data),
    delete: (id: string) =>
      request<void>('DELETE', `/api/chats/${id}`),
    /** Issue / rotate this chat's per-chat API token (spec/CHAT_API.md §5). */
    genApiToken: (id: string) =>
      request<{ token: string }>('POST', `/api/chats/${id}/api-token`),
    resume: (id: string) =>
      request<void>('POST', `/api/chats/${id}/resume`),
    restart: (id: string, cols?: number, rows?: number) =>
      request<{ message: string; chat: Chat }>('POST', `/api/chats/${id}/restart`, { cols, rows }),
    clean: (id: string) =>
      request<{ message: string; chatId: string }>('POST', `/api/chats/${id}/clean`),
    cleanAll: () =>
      request<{ message: string; cleaned: string[] }>('POST', `/api/chats/clean-all`),
    /** Drop stuck "sending/queued" submit rows for confirmed messages (manual recovery). */
    clearPending: (id: string) =>
      request<{ ok: boolean; cleared: number }>('POST', `/api/chats/${id}/clear-pending`),
    history: (id: string, before?: number, limit = 50) =>
      request<Message[]>(
        'GET',
        `/api/chats/${id}/history${before ? `?before=${before}&limit=${limit}` : `?limit=${limit}`}`,
      ),
    structuredHistory: (id: string, limit?: number, before?: number) => {
      const qs = new URLSearchParams()
      if (limit != null)  qs.set('limit', String(limit))
      if (before != null) qs.set('before', String(before))
      const q = qs.toString()
      return request<StructuredHistory>('GET', `/api/chats/${id}/structured-history${q ? `?${q}` : ''}`)
    },
    // WhimerTerm² committed-log pagination (scroll-up lazy-load). before = oldest id held (cursor).
    nativeHistory: (id: string, opts?: { limit?: number; before?: number }) => {
      const qs = new URLSearchParams()
      if (opts?.limit != null)  qs.set('limit', String(opts.limit))
      if (opts?.before != null) qs.set('before', String(opts.before))
      const q = qs.toString()
      return request<{ lines: NativeLine[]; firstId: number | null; hasMore: boolean }>(
        'GET', `/api/chats/${id}/native-history${q ? `?${q}` : ''}`)
    },
    suggestedPaths: (wsId: string) =>
      request<{ path: string; count: number }[]>('GET', `/api/workspaces/${wsId}/suggested-paths`),
    /** Read a Markdown file inside the chat's working dir (document viewer). */
    readDoc: (id: string, path: string) =>
      request<{ exists: boolean; name: string; size: number; content: string }>(
        'GET', `/api/chats/${id}/file?path=${encodeURIComponent(path)}`),
    /** Cheap existence/size check — cards use it to verify a .md path is real before showing. */
    statDoc: (id: string, path: string) =>
      request<{ exists: boolean; name?: string; size?: number; path?: string; candidates?: string[] }>(
        'GET', `/api/chats/${id}/file?meta=1&path=${encodeURIComponent(path)}`),
    /** Proxy-fetch a remote .md URL (avoids browser CORS) for the document viewer. */
    fetchMarkdown: (id: string, url: string) =>
      request<{ name: string; url: string; content: string }>(
        'GET', `/api/chats/${id}/fetch-md?url=${encodeURIComponent(url)}`),
  },
  plugins: {
    list: () =>
      request<Plugin[]>('GET', '/api/plugins'),
  },
  // Phase F switcher: routed models available for the "Model" picker (allowlist-filtered server-side).
  switcher: {
    routes: () =>
      request<SwitcherRoute[]>('GET', '/api/switcher/routes'),
  },
  // Declarative UI-widget slot contributions (C3) — registered into the frontend slot registry.
  slots: {
    list: () =>
      request<SlotContributionDTO[]>('GET', '/api/slots'),
  },
  // Account-level API key for the machine API (spec/CHAT_API.md §5): create + list chats.
  account: {
    getApiKey: () => request<{ apiKey: string | null }>('GET', '/api/account/api-key'),
    genApiKey: () => request<{ apiKey: string }>('POST', '/api/account/api-key'),
    clearApiKey: () => request<{ ok: boolean }>('DELETE', '/api/account/api-key'),
  },
  settings: {
    apiKeys: {
      list: (category?: string) =>
        request<ApiKeyRow[]>('GET', `/api/settings/api-keys${category ? `?category=${encodeURIComponent(category)}` : ''}`),
      set: (name: string, value: string, category?: string, nickname?: string, model?: string) =>
        request<{ name: string }>('POST', '/api/settings/api-keys', { name, value, category, nickname, model }),
      delete: (name: string) =>
        request<void>('DELETE', `/api/settings/api-keys/${encodeURIComponent(name)}`),
    },
    providers: {
      list: (category?: string) =>
        request<Provider[]>('GET', `/api/settings/providers${category ? `?category=${encodeURIComponent(category)}` : ''}`),
    },
    app: {
      get: () =>
        request<Record<string, string>>('GET', '/api/settings/app'),
      set: (key: string, value: string) =>
        request<{ key: string; value: string }>('PUT', `/api/settings/app/${encodeURIComponent(key)}`, { value }),
    },
    plugins: {
      list: () =>
        request<PluginSetting[]>('GET', '/api/settings/plugins'),
      setCwd: (name: string, cwd: string | null) =>
        request<{ name: string; cwd: string | null }>('PUT', `/api/settings/plugins/${encodeURIComponent(name)}/cwd`, { cwd }),
    },
    extensions: {
      list: () =>
        request<{ extensions: ExtensionInfo[] }>('GET', '/api/settings/extensions')
          .then(r => r.extensions),
      setEnabled: (name: string, enabled: boolean) =>
        request<ExtensionInfo>('PUT', `/api/settings/extensions/${encodeURIComponent(name)}`, { enabled }),
      checkUpdate: (name: string) =>
        request<ExtensionUpdateCheck>('GET', `/api/settings/extensions/${encodeURIComponent(name)}/check-update`),
    },
    recognizers: {
      list: () =>
        request<{ recognizers: RecognizerInfo[] }>('GET', '/api/settings/recognizers')
          .then(r => r.recognizers),
      setEnabled: (name: string, enabled: boolean) =>
        request<{ name: string; enabled: boolean }>('PUT', `/api/settings/recognizers/${encodeURIComponent(name)}`, { enabled }),
    },
    featurePlugins: {
      list: () =>
        request<{ features: FeaturePluginInfo[] }>('GET', '/api/settings/feature-plugins')
          .then(r => r.features),
      setEnabled: (id: string, enabled: boolean) =>
        request<{ id: string; enabled: boolean }>('PUT', `/api/settings/feature-plugins/${encodeURIComponent(id)}`, { enabled }),
    },
    voiceConfig: () =>
      request<VoiceConfig>('GET', '/api/settings/voice-config'),
    /** Server-side feature flags surfaced to the client (e.g. native-pty spawn gate). */
    features: () =>
      request<{ nativeSpawn: boolean }>('GET', '/api/settings/features'),
  },
}
